import { useBookingsCountQuery } from '@typings/graphql'
import React from 'react'
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Box,
  ListSubheader,
  Divider,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { LocalizationProvider, DateRangePicker, DateRange } from '@mui/x-date-pickers-pro'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { deDE } from '@mui/x-date-pickers/locales'
import { de } from 'date-fns/locale'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

import { StatisticCard } from './StatisticCard'
import {
  calculateTrend,
  ComparisonDirection,
  getComparisonPeriod,
  getCustomComparisonPeriod,
  getTimelineRange,
  normalizeDateRange,
  TIMELINE_LABELS,
  TimelineValue,
  Trend
} from './dateRangeUtils'
import { TrendCell } from './TrendCell'
import { useAvailableBookingYears } from './useAvailableBookingYears'

export const TourBookingsList: React.FC = () => {
  const { t } = useTranslation()
  const { years: availableYears, loading: yearsLoading } = useAvailableBookingYears()

  const [selectedTimeline, setSelectedTimeline] = React.useState<TimelineValue>('today')
  const [selectedDateRange, setSelectedDateRange] = React.useState<DateRange<Date> | null>(null)
  const [comparisonDirection, setComparisonDirection] = React.useState<ComparisonDirection>('backward')

  const usesCustomRange = Boolean(selectedDateRange?.[0] && selectedDateRange?.[1])

  const activeRange = React.useMemo(() => {
    if (usesCustomRange) {
      return normalizeDateRange(selectedDateRange as DateRange<Date>)
    }

    return normalizeDateRange(getTimelineRange(selectedTimeline))
  }, [selectedDateRange, selectedTimeline, usesCustomRange])

  const comparisonPeriod = React.useMemo(() => {
    if (usesCustomRange) {
      // Flexible range: compare against the same-length window before/after it.
      return getCustomComparisonPeriod(selectedDateRange as DateRange<Date>, comparisonDirection)
    }

    return getComparisonPeriod(selectedTimeline, comparisonDirection)
  }, [selectedDateRange, selectedTimeline, usesCustomRange, comparisonDirection])

  const showTrend = Boolean(comparisonPeriod)

  const comparisonHint = React.useMemo(() => {
    const start = comparisonPeriod?.comparison?.[0]
    const end = comparisonPeriod?.comparison?.[1]
    if (!start || !end) {
      return ''
    }

    const fmt = (date: Date) =>
      new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date)

    return t('payment.trendComparisonHint', { start: fmt(start), end: fmt(end) })
  }, [comparisonPeriod, t])

  // no-cache on both: two identical bookingsCount queries with different date ranges would
  // otherwise clobber each other in the normalized cache and flatten every trend to 0%.
  const { data, loading: currentLoading } = useBookingsCountQuery({
    fetchPolicy: 'no-cache',
    variables: {
      data: {
        startDate: activeRange?.[0],
        endDate: activeRange?.[1]
      }
    }
  })

  const { data: previousData, loading: previousLoading } = useBookingsCountQuery({
    skip: !comparisonPeriod,
    fetchPolicy: 'no-cache',
    variables: {
      data: {
        startDate: comparisonPeriod?.comparison[0],
        endDate: comparisonPeriod?.comparison[1]
      }
    }
  })

  const trendLoading = showTrend && (currentLoading || previousLoading)

  const trendByTourId = React.useMemo(() => {
    if (!showTrend || previousLoading || !data?.bookingsCount || !previousData?.bookingsCount) {
      return new Map<string, Trend>()
    }

    const previousCounts = new Map(
      previousData.bookingsCount.map((tour) => [tour.id, tour.count])
    )

    return new Map(
      data.bookingsCount.map((tour) => {
        const previousCount = previousCounts.get(tour.id) ?? 0
        return [tour.id, calculateTrend(tour.count, previousCount)]
      })
    )
  }, [data, previousData, showTrend, previousLoading])

  const overallTrend = React.useMemo(() => {
    if (!showTrend || previousLoading || !data?.bookingsCount || !previousData?.bookingsCount) {
      return null
    }

    const currentTotal = data.bookingsCount.reduce((sum, tour) => sum + tour.count, 0)
    const previousTotal = previousData.bookingsCount.reduce((sum, tour) => sum + tour.count, 0)

    return calculateTrend(currentTotal, previousTotal)
  }, [data, previousData, showTrend, previousLoading])

  const mappedData = React.useMemo(() => {
    if (!data?.bookingsCount) {
      return []
    }

    return [...data.bookingsCount]?.sort((a, b) => b.count - a.count).map((tour) => {
      return {
        id: tour.id,
        count: tour.count,
        value: `${tour.count} (${tour.percentage.toFixed(2)}%)`,
        label: tour.texts.title,
        trend: trendByTourId.get(tour.id) ?? null
      }
    })
  }, [data, trendByTourId])

  const handleTimelineChange = (value: TimelineValue) => {
    setSelectedTimeline(value)
    setSelectedDateRange(null)
  }

  const handleDateRangeChange = (value: DateRange<Date>) => {
    setSelectedDateRange(value)
  }

  const selectValue = selectedDateRange?.[0] ? '' : selectedTimeline

  return (
    <StatisticCard>
      <Stack
        spacing={2}
      >
        <Box
          p={2}
          pb={0}
          sx={{
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <FormControl variant='standard' sx={{ width: '33%' }}>
            <InputLabel htmlFor="my-input">{t('payment.bookingTimeline')}</InputLabel>
            <Select
              onChange={(e) => handleTimelineChange(e.target.value as TimelineValue)}
              value={selectValue}
            >
              {TIMELINE_LABELS.map((label) => (
                <MenuItem key={label} value={label}>
                  {t(`payment.timeline.${label}`)}
                </MenuItem>
              ))}

              {!yearsLoading && availableYears.length > 0 && [
                <Divider key="year-divider" />,
                <ListSubheader key="year-header">{t('payment.timeline.years')}</ListSubheader>,
                ...availableYears.map((year) => (
                  <MenuItem key={`year-${year}`} value={`year-${year}`}>
                    {year}
                  </MenuItem>
                ))
              ]}
            </Select>
          </FormControl>
          <div>
            <LocalizationProvider
              localeText={deDE.components.MuiLocalizationProvider.defaultProps.localeText}
              adapterLocale={de}
              dateAdapter={AdapterDateFns}
            >
              <DateRangePicker
                sx={{
                  width: '100%'
                }}
                value={selectedDateRange ?? [null, null]}
                onChange={(value) => handleDateRangeChange(value)}
              />
            </LocalizationProvider>
          </div>
        </Box>
        <TableContainer
          sx={{
            px: 2,
            pb: 2,
            maxHeight: 440,
            overflow: 'auto',
            '.MuiTableCell-root': {
              padding: '8px 0'
            }
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('payment.title')}</TableCell>
                <TableCell align="right">{t('payment.bookingCount')}</TableCell>
                {showTrend && (
                  <TableCell align="right">
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                      {t('payment.trend')}
                      <Tooltip title={comparisonHint}>
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={comparisonDirection}
                        onChange={(_, value) => value && setComparisonDirection(value)}
                        sx={{ ml: 0.5, '.MuiToggleButton-root': { p: 0.25, border: 'none' } }}
                      >
                        <ToggleButton value="backward">
                          <Tooltip title={t('payment.compareBackward')}>
                            <ArrowBackIcon sx={{ fontSize: 16 }} />
                          </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="forward">
                          <Tooltip title={t('payment.compareForward')}>
                            <ArrowForwardIcon sx={{ fontSize: 16 }} />
                          </Tooltip>
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {mappedData.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    ...(row.id === mappedData[mappedData.length - 1].id && {
                      'td, th': {
                        borderBottom: '1px solid #000'
                      }
                    })
                  }}
                >
                  <TableCell component="th" scope="row">
                    {row.label}
                  </TableCell>
                  <TableCell align="right">{row.value}</TableCell>
                  {showTrend && (
                    <TableCell align="right">
                      {trendLoading || row.trend === null ? '—' : <TrendCell value={row.trend} />}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {mappedData.length > 0 && (
              <TableRow
                sx={{
                  'td, th': { border: 'none' }
                }}
              >
                <TableCell component="th" scope="row">{t('payment.overall')}</TableCell>
                <TableCell align="right">{mappedData.reduce((acc, row) => acc + row.count, 0)}</TableCell>
                {showTrend && (
                  <TableCell align="right">
                    {trendLoading || overallTrend === null ? '—' : <TrendCell value={overallTrend} />}
                  </TableCell>
                )}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </StatisticCard>
  )
}
